import { useGetCategories, useGetProducts } from '@agos/api-client/menu'
import { tt } from '@agos/shared'
import { PageHeader, ReadFailedBlock, Spinner, TabGroup } from '@agos/ui'
import { useQuery } from '@tanstack/react-query'
import { Copy, FolderTree, Layers } from 'lucide-react'
import { useState } from 'react'

import { CopyPanel } from './CopyPanel'
import { GroupsPanel } from './GroupsPanel'
import { ItemsPanel, type Editing } from './ItemsPanel'
import {
  UPSELL_GROUPS_KEY,
  UPSELL_ITEMS_KEY,
  fetchUpsellGroups,
  fetchUpsellItems,
} from './upsell-api'

type Tab = 'items' | 'groups' | 'copy'

/**
 * *Upsell produktov* - every upsell on one screen - `FRONTEND.md` §9.711, owner O86, O87 and O91.
 *
 * **The operator's design**: *"in Správa kuchyne new tab Upsell Produktov ... and that you can assign multiple
 * products on one upsell item"*. So the screen starts from the **add-on** rather than from the product - the
 * reverse view of `product_addons` §10A.364 measured as 27 add-ons over 2 239 links at Šurany - and one save
 * assigns one add-on to many products.
 *
 * **Four reads, and the screen draws nothing over a failed one.** Every panel needs the items, the products (for
 * names), the groups (for the selector) and the categories (for the checklist's order), so a failure replaces the
 * panels rather than sitting beside a checklist that would show an add-on offered with nothing - which is the
 * claim a save from that checklist would then make true. One block per failed read, each with its own retry,
 * because they are different faults.
 */
export function UpsellPage() {
  const items = useQuery({ queryKey: UPSELL_ITEMS_KEY, queryFn: fetchUpsellItems })
  const groups = useQuery({ queryKey: UPSELL_GROUPS_KEY, queryFn: fetchUpsellGroups })
  const products = useGetProducts()
  const categories = useGetCategories()
  const [tab, setTab] = useState<Tab>('items')
  const [editing, setEditing] = useState<Editing | null>(null)

  const reads = [
    { of: 'items', isError: items.isError, retry: () => void items.refetch(), what: tt('Doplnky sa nepodarilo načítať.') },
    { of: 'groups', isError: groups.isError, retry: () => void groups.refetch(), what: tt('Skupiny sa nepodarilo načítať.') },
    { of: 'products', isError: products.isError, retry: () => void products.refetch(), what: tt('Produkty sa nepodarilo načítať.') },
    { of: 'categories', isError: categories.isError, retry: () => void categories.refetch(), what: tt('Kategórie sa nepodarilo načítať.') },
  ]
  const failed = reads.filter((read) => read.isError)
  const loading = items.isLoading || groups.isLoading || products.isLoading || categories.isLoading

  const itemList = items.data ?? []
  const groupList = groups.data ?? []
  const productList = products.data ?? []
  const categoryList = categories.data ?? []

  return (
    <main className="space-y-4 p-4" data-upsell-page>
      <PageHeader
        title={tt('Upsell produktov')}
        description={tt(
          'Doplnky, ktoré sa ponúknu k produktu: pri ktorých produktoch, v akom poradí a v akých skupinách.',
        )}
        icon={<Layers className="w-5 h-5" />}
      />

      {failed.length > 0 ? (
        <div className="space-y-2">
          {failed.map((read) => (
            <ReadFailedBlock
              key={read.of}
              marker="upsell"
              of={read.of}
              title={tt('Upsell sa nepodarilo načítať')}
              onRetry={read.retry}
            >
              {read.what}{' '}
              {tt('Bez toho by sa zoznam doplnkov alebo produktov ukázal prázdny a uloženie by ho takým urobilo.')}
            </ReadFailedBlock>
          ))}
        </div>
      ) : loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <>
          <TabGroup
            variant="bar"
            active={tab}
            onChange={(next) => setTab(next as Tab)}
            tabs={[
              { id: 'items', label: tt('Doplnky'), icon: Layers, count: itemList.length },
              { id: 'groups', label: tt('Skupiny'), icon: FolderTree, count: groupList.length },
              { id: 'copy', label: tt('Kopírovať doplnky'), icon: Copy },
            ]}
          />
          {tab === 'items' && (
            <ItemsPanel
              items={itemList}
              groups={groupList}
              products={productList}
              categories={categoryList}
              editing={editing}
              onEdit={setEditing}
            />
          )}
          {tab === 'groups' && (
            <GroupsPanel
              groups={groupList}
              items={itemList}
              products={productList}
              onEditItem={(next) => {
                setEditing(next)
                setTab('items')
              }}
            />
          )}
          {tab === 'copy' && (
            <CopyPanel items={itemList} groups={groupList} products={productList} categories={categoryList} />
          )}
        </>
      )}
    </main>
  )
}
